import type { Building } from "building-types/building/buildingTypes";
import type { RoomSet, RoomConnection } from "building-types/rooms/roomTraversalTypes";
import { RoomConnectionType } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { Wall } from "building-types/walls/wallTypes";
import { getWallPath } from "building-types/walls/wallUtils";
import type { Table } from "common/data/table";
import { aabb2Union } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { polyLine2AABB2 } from "geom/polyLine2";

export function roomSetTopDownAabb(roomSet: RoomSet): AABB2 {
  let totalAabb = polyLine2AABB2((roomSet[0] as Room).outline);
  for (const affectedRoom of roomSet) {
    const roomAabb = polyLine2AABB2((affectedRoom as Room).outline);
    totalAabb = aabb2Union(totalAabb, roomAabb);
  }
  return totalAabb;
}

export function getOpenConnectedRoomSets(
  levelRooms: Room[],
  levelRoomConnections: RoomConnection[][],
  roomLevelIndices: Table<number>,
): RoomSet[] {
  const openConnectedRoomSets: RoomSet[] = [];

  for (const room of levelRooms) {
    const roomSet: RoomSet = [];

    // create room group
    traverseRoomsOnLevel(
      room,
      levelRoomConnections,
      levelRooms,
      roomLevelIndices,
      (_, conn) => conn.connectionType === RoomConnectionType.OPEN,
      (r) => roomSet.push(r),
    );

    // process new group
    if (roomSet.length > 0) {
      for (const room of roomSet) {
        openConnectedRoomSets[roomLevelIndices[room.id]] = roomSet;
      }
    }
  }

  return openConnectedRoomSets;
}

function traverseRoomsOnLevel(
  currentRoom: Room | undefined,
  levelRoomConnections: RoomConnection[][],
  levelRooms: Room[],
  roomLevelIndices: Table<number>,
  traversalPredicate: (room: Room, conn: RoomConnection) => boolean,
  traversalFunc: (room: Room) => void,
  visited: { [key: number]: boolean } = {},
) {
  if (!currentRoom || visited[roomLevelIndices[currentRoom.id]]) {
    return;
  }

  traversalFunc(currentRoom);
  visited[roomLevelIndices[currentRoom.id]] = true;

  for (const connection of levelRoomConnections[roomLevelIndices[currentRoom.id]]) {
    if (traversalPredicate(currentRoom, connection)) {
      traverseRoomsOnLevel(
        connection.roomIndexOnLevel !== undefined ? levelRooms[connection.roomIndexOnLevel] : undefined,
        levelRoomConnections,
        levelRooms,
        roomLevelIndices,
        traversalPredicate,
        traversalFunc,
        visited,
      );
    }
  }
}

export function groupRoomsOnLevel(
  currentRoom: Room,
  wallSystem: Building["walls"],
  traversalPredicate: (startRoom: Room, endRoom: Room, wall: Wall) => boolean,
  traversalFunc: (room: Room) => void,
  visitedRooms: { [roomId: string]: boolean } = {},
) {
  if (!currentRoom || visitedRooms[currentRoom.id]) {
    return;
  }

  const wallIndex = wallSystem[currentRoom.level.id].wallIndex;
  const roomWalls = wallIndex.wallsInRoom(currentRoom);

  traversalFunc(currentRoom);
  visitedRooms[currentRoom.id] = true;

  for (const wall of roomWalls) {
    const path = getWallPath(wall);
    const leftRoom = path.leftRoom;
    const rightRoom = path.rightRoom;
    const endRoom = currentRoom.id === leftRoom?.id ? rightRoom : leftRoom;

    if (endRoom && traversalPredicate(currentRoom, endRoom, wall)) {
      groupRoomsOnLevel(endRoom, wallSystem, traversalPredicate, traversalFunc, visitedRooms);
    }
  }
}
